import React, { useEffect, useRef, useState } from "react";
import { MinusOutlined, PlusOutlined, TeamOutlined } from "@ant-design/icons";
import { createDefaultRooms, summarizeOccupancy } from "./demoFixtures";

function CounterRow({ label, hint, value, min, max, onChange }) {
  return (
    <div className="hrm-occ__row">
      <div className="hrm-occ__labels">
        <span className="hrm-occ__label">{label}</span>
        {hint ? <span className="hrm-occ__hint">{hint}</span> : null}
      </div>
      <div className="hrm-occ__counter" role="group" aria-label={label}>
        <button
          type="button"
          className="hrm-occ__btn"
          aria-label={`הפחתת ${label}`}
          disabled={value <= min}
          onClick={() => onChange(value - 1)}
        >
          <MinusOutlined />
        </button>
        <span className="hrm-occ__value" aria-live="polite">
          {value}
        </span>
        <button
          type="button"
          className="hrm-occ__btn"
          aria-label={`הוספת ${label}`}
          disabled={value >= max}
          onClick={() => onChange(value + 1)}
        >
          <PlusOutlined />
        </button>
      </div>
    </div>
  );
}

/**
 * Phase 0 occupancy — rooms with adults + childAges per room.
 * Uses HRM occupancy chrome classes; lives only in unifiedSearchMockup.
 */
export default function UnifiedOccupancyField({ rooms, onChange, disabled = false }) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() =>
    Array.isArray(rooms) && rooms.length ? rooms.map((r) => ({ ...r })) : createDefaultRooms()
  );

  useEffect(() => {
    setDraft(
      Array.isArray(rooms) && rooms.length
        ? rooms.map((r) => ({
            adults: Math.max(1, Number(r.adults) || 1),
            childAges: Array.isArray(r.childAges) ? [...r.childAges] : [],
          }))
        : createDefaultRooms()
    );
  }, [rooms]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const triggerLabel = summarizeOccupancy(rooms).occupancyLabel;

  const confirm = () => {
    const next = draft.map((r) => ({
      adults: Math.max(1, Number(r.adults) || 1),
      childAges: (r.childAges || []).map((a) => Math.max(0, Math.min(17, Number(a) || 0))),
    }));
    onChange?.(next);
    setOpen(false);
  };

  const updateRoom = (index, patch) => {
    setDraft((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const setChildCount = (index, count) => {
    setDraft((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const ages = [...(r.childAges || [])];
        while (ages.length < count) ages.push(8);
        return { ...r, childAges: ages.slice(0, count) };
      })
    );
  };

  return (
    <div className="hrm-picker usm-occ" ref={rootRef}>
      <button
        type="button"
        className={`hrm-picker__trigger${open ? " is-open" : ""}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <TeamOutlined aria-hidden />
        <span>{triggerLabel}</span>
      </button>

      {open ? (
        <div className="hrm-occ usm-occ__panel" role="dialog" aria-label="הרכב נוסעים וחדרים" dir="rtl">
          {draft.map((room, index) => (
            <div className="hrm-occ__section" key={`room-${index}`}>
              <p className="hrm-occ__section-title">חדר {index + 1}</p>
              <CounterRow
                label="מבוגרים"
                value={room.adults}
                min={1}
                max={6}
                onChange={(v) => updateRoom(index, { adults: v })}
              />
              <CounterRow
                label="ילדים"
                hint="גילאי 0–17"
                value={(room.childAges || []).length}
                min={0}
                max={4}
                onChange={(v) => setChildCount(index, v)}
              />
              {(room.childAges || []).map((age, ageIdx) => (
                <label className="usm-occ__age" key={`age-${index}-${ageIdx}`}>
                  <span>גיל ילד {ageIdx + 1}</span>
                  <select
                    value={age}
                    onChange={(e) => {
                      const nextAges = [...room.childAges];
                      nextAges[ageIdx] = Number(e.target.value);
                      updateRoom(index, { childAges: nextAges });
                    }}
                  >
                    {Array.from({ length: 18 }).map((_, a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
              {draft.length > 1 ? (
                <button
                  type="button"
                  className="usm-occ__remove-room"
                  onClick={() => setDraft((prev) => prev.filter((_, i) => i !== index))}
                >
                  הסר חדר
                </button>
              ) : null}
            </div>
          ))}

          {draft.length < 4 ? (
            <button
              type="button"
              className="usm-occ__add-room"
              onClick={() =>
                setDraft((prev) => [...prev, { adults: 2, childAges: [] }])
              }
            >
              + הוסף חדר
            </button>
          ) : null}

          <button type="button" className="hrm-occ__confirm" onClick={confirm}>
            אישור
          </button>
        </div>
      ) : null}
    </div>
  );
}
